import { rdfPatchEdit } from "../../../api/content/changeSetsApi";
import { getItemVersionById } from "../../../api/content/itemsApi";
import type { TermDetails } from "../../../api/content/ontologyApi";
import { toUiError } from "../../../errors/uiError";
import { parseDocumentVersionRef, type DocumentVersionRef } from "../../../shared/references/contentReferences";
import type { CommitBuilder } from "../../changeSets/changeSetChoice";
import { patchOfTermEdit, targetModuleOf, type TermEdit, type TriplePatch } from "./termPatches";

/**
 * The commit of `edits` of one term, as one triple patch per module they belong in, for the
 * one change set path (`commitToChangeSet`, saved through the "Edit an item" step). A new
 * change set takes each patch on the module version shown; an existing one on the module
 * version at its head, so earlier edits there stay.
 */
export function termEditCommit(projectId: string, details: TermDetails, edits: readonly TermEdit[], ownModules: ReadonlySet<string>): CommitBuilder {
  const byModule = patchesByModule(details, edits, ownModules);
  return async (head) => {
    const patches = [];
    for (const { module, patch } of byModule.values()) {
      const basedOn = head === null ? module.version : ((await moduleVersionAt(projectId, head, module.documentId)) ?? module.version);
      patches.push(rdfPatchEdit({ itemId: module.documentId, basedOn, ...patch }));
    }
    return patches;
  };
}

/** The module's version at `commit`; `null` for a module the change set removed, whose commit the server then refuses, saying why. */
async function moduleVersionAt(projectId: string, commit: string, moduleId: string): Promise<string | null> {
  try {
    return parseDocumentVersionRef((await getItemVersionById(projectId, moduleId, commit)).reference)?.version ?? null;
  } catch (caught: unknown) {
    const error = toUiError(caught);
    if (error.kind === "problem" && error.problem.code === "resource_not_found") return null;
    throw caught;
  }
}

/** Each module's edits as one patch, its triples in the order of the edits. */
function patchesByModule(
  details: TermDetails,
  edits: readonly TermEdit[],
  ownModules: ReadonlySet<string>,
): Map<string, { readonly module: DocumentVersionRef; readonly patch: TriplePatch }> {
  const byModule = new Map<string, { readonly module: DocumentVersionRef; readonly patch: TriplePatch }>();
  for (const edit of edits) {
    const target = targetModuleOf(details, edit, ownModules);
    if (target === null) {
      throw new Error("Only an inherited module holds this statement; the project can't change it.");
    }
    const module = parseDocumentVersionRef(target);
    if (module === null) {
      throw new Error("The server named a module without an exact version.");
    }
    const patch = patchOfTermEdit(details.term.iri, edit);
    const earlier = byModule.get(target)?.patch;
    byModule.set(target, {
      module,
      patch: earlier === undefined ? patch : { addTurtle: earlier.addTurtle + patch.addTurtle, removeTurtle: earlier.removeTurtle + patch.removeTurtle },
    });
  }
  return byModule;
}
